import * as THREE from "three";
import type { Mood } from "./dayCycle";
import type { WeatherType } from "./voxelTypes";

/**
 * The village's weather, derived rather than chosen.
 *
 * The reference app made this four buttons, which suits a scene viewer. It does not suit a place
 * a guild shares: the day cycle here runs on the UTC clock precisely so that someone saying
 * "come and look at the sunset" is describing what everybody else can see, and weather someone
 * could switch off privately would break the same promise for rain.
 *
 * So the sky is a function of the clock and the guild's own world seed. Every member of a guild
 * sees the same weather at the same moment, and can be told a week in advance what is coming,
 * without the server ever being asked — the forecast is not fetched, it is recomputed.
 *
 * A day is the unit, not an hour. Each day draws a *pattern*, and the pattern says how the day
 * runs: rain that arrives after a clear morning, rain that clears up, a storm that builds out of
 * rain, or snow that settles in for the whole day. Weather that flickered between states every
 * few hours would make a forecast meaningless, and a village that cannot be planned around is
 * just noise on the screen.
 */

const DAY_MS = 24 * 3600_000;

/** The shape of a day. */
export type DayPattern = "clear" | "rain-arriving" | "rain-clearing" | "storm" | "snow";

/**
 * The odds, out of 100.
 *
 * Overwhelmingly fine: rain is a change of scene, and a village wet more than about a third of
 * the time reads as dreary rather than cosy. Snow is deliberately the rare one — it repaints the
 * entire village white, so it should feel like an occasion rather than a Tuesday.
 */
const ODDS: { pattern: DayPattern; weight: number }[] = [
  { pattern: "clear", weight: 55 },
  { pattern: "rain-arriving", weight: 16 },
  { pattern: "rain-clearing", weight: 15 },
  { pattern: "storm", weight: 9 },
  { pattern: "snow", weight: 5 }
];

/** When a day is allowed to turn, in UTC hours. Kept to daylight so the change is seen. */
const TURN_EARLIEST = 8;
const TURN_LATEST = 16;

/** How long a thunderstorm holds at its worst before easing back to ordinary rain. */
const STORM_HOURS = 4;

/** A stretch of one day during which the weather does not change. */
export type WeatherSegment = {
  /** UTC hour this stretch begins. The first is always 0. */
  fromHour: number;
  weather: WeatherType;
};

export type DayForecast = {
  /** Whole UTC days since the epoch — the day's identity, and what is hashed. */
  dayIndex: number;
  pattern: DayPattern;
  /** Midnight UTC at the start of this day. */
  startsAt: number;
  segments: WeatherSegment[];
};

/** The guild's seed as a number. Worlds are seeded with a string; the arithmetic below is not. */
function seedNumber(seed: string): number {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (Math.imul(hash, 31) + seed.charCodeAt(index)) | 0;
  }
  return hash;
}

/**
 * A stable hash of a day, a seed and a salt, so every client agrees without asking the server.
 *
 * The salt gives each day two independent draws — which pattern, and when it turns — from the
 * same day number, without one leaking into the other.
 */
function dayHash(dayIndex: number, seed: number, salt: number): number {
  let hash = Math.imul(dayIndex ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(seed | 0, 0xc2b2ae35);
  hash = Math.imul(hash ^ salt, 0x27d4eb2d);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0x5bd1e995);
  hash ^= hash >>> 15;
  return (hash >>> 0) / 0x100000000;
}

function patternFor(dayIndex: number, seed: number): DayPattern {
  const roll = dayHash(dayIndex, seed, 0x01) * 100;
  let running = 0;
  for (const entry of ODDS) {
    running += entry.weight;
    if (roll < running) return entry.pattern;
  }
  return "clear";
}

/** Expands a pattern into the day's stretches of weather. */
function segmentsFor(pattern: DayPattern, turnHour: number): WeatherSegment[] {
  switch (pattern) {
    case "rain-arriving":
      return [{ fromHour: 0, weather: "clear" }, { fromHour: turnHour, weather: "rain" }];
    case "rain-clearing":
      return [{ fromHour: 0, weather: "rain" }, { fromHour: turnHour, weather: "clear" }];
    case "storm":
      // A storm is weather that builds and passes: it rains first, the storm breaks, and the
      // day ends as rain again. Arriving out of a clear sky, or holding at full storm until
      // midnight, both read as a switch being thrown rather than as weather.
      return [
        { fromHour: 0, weather: "rain" },
        { fromHour: turnHour, weather: "storm" },
        ...(turnHour + STORM_HOURS < 24
          ? [{ fromHour: turnHour + STORM_HOURS, weather: "rain" as WeatherType }]
          : [])
      ];
    case "snow":
      return [{ fromHour: 0, weather: "snow" }];
    default:
      return [{ fromHour: 0, weather: "clear" }];
  }
}

/** The whole shape of one day, by its UTC day number. */
export function forecastForDay(dayIndex: number, seed: string): DayForecast {
  const seedValue = seedNumber(seed);
  const pattern = patternFor(dayIndex, seedValue);
  const spread = TURN_LATEST - TURN_EARLIEST;
  const turnHour = TURN_EARLIEST + Math.floor(dayHash(dayIndex, seedValue, 0x02) * (spread + 1));
  return {
    dayIndex,
    pattern,
    startsAt: dayIndex * DAY_MS,
    segments: segmentsFor(pattern, turnHour)
  };
}

/** The week ahead, starting with the day currently in progress. */
export function weekAhead(epochMs: number, seed: string, days = 7): DayForecast[] {
  const today = Math.floor(epochMs / DAY_MS);
  return Array.from({ length: days }, (_, offset) => forecastForDay(today + offset, seed));
}

/** The weather at a given moment, as a whole guild sees it. */
export function weatherAt(epochMs: number, seed: string): WeatherType {
  const day = forecastForDay(Math.floor(epochMs / DAY_MS), seed);
  const hour = (epochMs - day.startsAt) / 3600_000;
  let current: WeatherType = "clear";
  for (const segment of day.segments) {
    if (segment.fromHour > hour) break;
    current = segment.weather;
  }
  return current;
}

/** When the weather next changes, or null if the day holds. Used to word "rain from four". */
export function nextChange(epochMs: number, seed: string): { at: number; weather: WeatherType } | null {
  const day = forecastForDay(Math.floor(epochMs / DAY_MS), seed);
  const hour = (epochMs - day.startsAt) / 3600_000;
  for (const segment of day.segments) {
    if (segment.fromHour > hour) return { at: day.startsAt + segment.fromHour * 3600_000, weather: segment.weather };
  }
  return null;
}

/** The weather now, with a `?weather=` override in development so a look can be checked. */
export function currentWeather(seed: string): WeatherType {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("weather");
    if (override === "clear" || override === "rain" || override === "snow" || override === "storm") {
      return override;
    }
  }
  return weatherAt(Date.now(), seed);
}

/**
 * What weather does to the light.
 *
 * These are multipliers and pulls rather than the reference app's table of absolute colours: it
 * hardcoded all sixteen combinations of four skies and four weathers, which cannot work here
 * because the mood is interpolated continuously across the day and is never one of four things.
 * Overcast light is flatter, greyer and dimmer than clear light at any hour, so saying that once
 * as a transformation covers every minute of the cycle.
 */
type WeatherLook = {
  /** Colour the sky and fog are pulled toward, and how far. */
  tint: number;
  tintAmount: number;
  fogScale: number;
  sunScale: number;
  ambientScale: number;
  /** Lamps and windows come up early when it is dark enough outside. */
  lampBoost: number;
};

const LOOKS: Record<WeatherType, WeatherLook> = {
  clear: { tint: 0x000000, tintAmount: 0, fogScale: 1, sunScale: 1, ambientScale: 1, lampBoost: 0 },
  rain: { tint: 0x6b7d8d, tintAmount: 0.55, fogScale: 2.2, sunScale: 0.62, ambientScale: 0.86, lampBoost: 1.5 },
  snow: { tint: 0xc0d8e8, tintAmount: 0.6, fogScale: 1.9, sunScale: 0.85, ambientScale: 1.05, lampBoost: 1 },
  storm: { tint: 0x3d4756, tintAmount: 0.8, fogScale: 3.4, sunScale: 0.32, ambientScale: 0.58, lampBoost: 3.5 }
};

/** The mood as the weather leaves it. */
export function applyWeather(mood: Mood, weather: WeatherType): Mood {
  const look = LOOKS[weather];
  if (look.tintAmount === 0) return mood;

  const tint = new THREE.Color(look.tint);
  return {
    ...mood,
    sky: mood.sky.clone().lerp(tint, look.tintAmount),
    fogDensity: mood.fogDensity * look.fogScale,
    sunColor: mood.sunColor.clone().lerp(tint, look.tintAmount * 0.7),
    sunIntensity: mood.sunIntensity * look.sunScale,
    ambientColor: mood.ambientColor.clone().lerp(tint, look.tintAmount * 0.7),
    ambientIntensity: mood.ambientIntensity * look.ambientScale,
    hemiSky: mood.hemiSky.clone().lerp(tint, look.tintAmount),
    hemiIntensity: mood.hemiIntensity * look.ambientScale,
    emissiveIntensity: mood.emissiveIntensity + look.lampBoost,
    lampIntensity: mood.lampIntensity + look.lampBoost
  };
}
